import { useEffect, useState } from 'react';
import { EmptyState } from '@/shared/components';
import { useAuth } from '@/shared/auth';
import { useApi, type PropertyGroupResponse } from '@/shared/api';
import { CommunicationUnsavedGuard } from '../communication-catalog/CommunicationUnsavedGuard';
import { brandingProblem } from './brandingProblem';
import { PortalBrandingEditor } from './PortalBrandingEditor';
import { PortalContentList } from './PortalContentList';
import { PortalPreview } from './PortalPreview';
import { usePortalBranding } from './usePortalBranding';

/** Bereich „Gastportal": die Marke, was im Portal steht, und wie es ein Gast sieht. */
export function PortalBrandingPanel({
  group,
  editable,
  canOpenSources,
}: {
  group: PropertyGroupResponse;
  editable: boolean;
  canOpenSources: boolean;
}) {
  const auth = useAuth();
  const api = useApi();
  const state = usePortalBranding(group.id);
  const [selectedId, setSelectedId] = useState(group.apartments[0]?.id ?? '');
  useEffect(() => setSelectedId(group.apartments[0]?.id ?? ''), [group.id, group.apartments]);

  if (state.query.isLoading) {
    return (
      <section
        className="border-line bg-card min-h-64 animate-pulse rounded-lg border"
        aria-label="Gastportal-Branding wird geladen"
      />
    );
  }
  if (!state.query.data) {
    return (
      <EmptyState
        icon="Error"
        title="Gastportal-Branding konnte nicht geladen werden"
        description="Bitte laden Sie die Seite erneut."
      />
    );
  }

  const branding = state.query.data;
  const logoUrl =
    branding.hasLogo && branding.logoVersion != null
      ? api.propertyGroups.guestExperience.logoUrl(group.id, branding.logoVersion)
      : null;
  const apartment = group.apartments.find((item) => item.id === selectedId) ?? group.apartments[0];
  const conflict = state.problem?.status === 409;
  // Ein gescheitertes Neuladen trägt seinen eigenen Satz; alles andere wird übersetzt.
  const error = state.problem
    ? state.reloadFailed
      ? (state.problem.detail ?? brandingProblem(state.problem))
      : brandingProblem(state.problem)
    : null;

  return (
    <section aria-labelledby="portal-branding-title" className="space-y-4">
      <div>
        <h2 id="portal-branding-title" className="text-ink font-serif text-2xl">
          Gastportal
        </h2>
        <p className="text-ink-soft mt-1 max-w-3xl text-sm">
          Wie Gäste das Portal dieses Objekts sehen. Die Marke gilt nur hier; Inhalte kommen aus den
          Gastinfos und werden nicht kopiert.
        </p>
      </div>
      <div className="@container">
        <div className="@4xl:grid-cols-[minmax(0,1fr)_20rem] grid items-start gap-6">
          <div className="min-w-0 space-y-5">
            <PortalBrandingEditor
              branding={branding}
              brandName={state.brandName}
              editable={editable}
              dirty={state.dirty}
              busy={state.save.isPending || state.upload.isPending || state.remove.isPending}
              error={error}
              conflict={conflict || state.reloadFailed}
              onBrandNameChange={state.changeBrandName}
              onSave={() => state.save.mutate()}
              onUpload={(file) => state.upload.mutate(file)}
              onRemove={() => state.remove.mutate()}
              onReload={() => void state.reload()}
            />
            <PortalContentList
              group={group}
              apartment={apartment}
              canOpenSources={canOpenSources}
            />
          </div>
          <PortalPreview
            group={group}
            selectedId={selectedId}
            branding={branding}
            logoUrl={logoUrl}
            token={auth.accessToken()}
            canReadSources={canOpenSources}
            onSelectedIdChange={setSelectedId}
          />
        </div>
      </div>
      <CommunicationUnsavedGuard dirty={state.dirty} />
    </section>
  );
}
